import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, TrendingUp, CheckCircle, Zap, FileText, Users,
  Lock, Globe, Brain, ArrowRight, Building2, Database,
  Fingerprint, BookOpen, Target, Gauge, ListChecks,
  ChevronDown, ChevronUp, GraduationCap, Bot, Network,
  Award, Search, AlertTriangle, Cpu, Handshake, Headphones,
  RefreshCw, ClipboardList, GitBranch, Eye, AlertCircle,
  Settings, UserCircle, Layers, Lightbulb, Activity,
  BarChart3, Briefcase, Landmark, Factory, Store,
  DollarSign, Leaf, UserCog, CloudCheck, ShieldCheck,
  Crosshair, FileSearch, Sparkles, UserCheck, Scale, X
} from 'lucide-react';
import { useAuth } from '@/App';

// Executive Chrome color constant
const CHROME = '#0A1F44';

// Regulatory Intelligence Capabilities
const CAPABILITIES = [
  {
    title: 'Regulatory Universe',
    description: 'Dynamic mapping of applicable regulations across jurisdictions with intelligent applicability assessment.',
    icon: Globe,
    features: ['Automated regulatory tracking', 'Applicability engine', 'Change management']
  },
  {
    title: 'Obligation Management',
    description: 'Extract, classify and operationalise regulatory obligations with AI-powered parsing.',
    icon: ListChecks,
    features: ['AI extraction', 'Provision mapping', 'Requirement tracking']
  },
  {
    title: 'Control Framework',
    description: 'Link controls to regulatory requirements with evidence-based assurance.',
    icon: Shield,
    features: ['Control library', 'Testing workflows', 'Gap analysis']
  },
  {
    title: 'Evidence Repository',
    description: 'Centralised, auditable evidence collection with immutable provenance tracking.',
    icon: Database,
    features: ['Document management', 'Version control', 'Audit trails']
  },
  {
    title: 'Risk Intelligence',
    description: 'Quantify and monitor regulatory risk exposure across your enterprise.',
    icon: Activity,
    features: ['Risk scoring', 'Heat maps', 'Trend analysis']
  },
  {
    title: 'Regulatory Reporting',
    description: 'Generate compliance reports, dashboards and board packs with confidence.',
    icon: BarChart3,
    features: ['Automated reports', 'Executive dashboards', 'Regulator submissions']
  }
];

// Trust Indicators
const TRUST_INDICATORS = [
  { label: 'Regulations Tracked', value: '500+', suffix: '' },
  { label: 'Obligations Managed', value: '50K', suffix: '+' },
  { label: 'Audit Ready', value: '100', suffix: '%' },
  { label: 'Uptime SLA', value: '99.9', suffix: '%' }
];

// Client Segments
const SEGMENTS = [
  { name: 'Financial Services', icon: Landmark },
  { name: 'Insurance', icon: ShieldCheck },
  { name: 'Asset Management', icon: TrendingUp },
  { name: 'Fintech', icon: Cpu },
  { name: 'Listed Companies', icon: Building2 },
  { name: 'Public Sector', icon: Scale }
];

const AURALanding = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSignInClick = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setShowAccessModal(true);
    }
  };

  const handleAdminPreviewAccess = async () => {
    setIsLoggingIn(true);
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: 'admin@navigateaura.com',
          password: 'Navigate2026!'
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (login) login(data.token, data.user);
        setShowAccessModal(false);
        navigate('/dashboard');
      } else {
        setLoginError(data.detail || 'Failed to access. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Connection error. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      {/* Navigation */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-white/95 backdrop-blur-sm"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-aura-deep flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <span className="font-display font-bold text-lg tracking-tight text-aura-deep">AURA</span>
                <span className="font-display font-light text-lg tracking-tight text-aura-slate ml-1">GRC</span>
              </div>
            </div>
            
            {/* Nav Links */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#capabilities" className="text-sm text-aura-slate hover:text-aura-deep transition-colors">
                Capabilities
              </a>
              <a href="#intelligence" className="text-sm text-aura-slate hover:text-aura-deep transition-colors">
                Intelligence
              </a>
              <a href="https://navigatecompliance.io/contact/" target="_blank" rel="noopener noreferrer" 
                 className="text-sm text-aura-slate hover:text-aura-deep transition-colors">
                Contact
              </a>
            </nav>
            
            {/* CTA */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleSignInClick}
                className="px-5 py-2 bg-aura-deep hover:bg-aura-ink text-white font-medium text-sm rounded transition-colors"
                data-testid="sign-in-btn"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8 relative overflow-hidden">
        {/* Grid Pattern Background */}
        <div className="absolute inset-0 aura-grid-pattern opacity-50" />
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-left"
            >
              {/* Overline */}
              <span className="aura-overline text-aura-teal mb-4 block">
                Regulatory Intelligence Platform
              </span>
              
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-aura-ink leading-[1.1] mb-6">
                Governance, Risk & Compliance
                <span className="block aura-gradient-text">Operationalised</span>
              </h1>
              
              <p className="text-lg text-aura-slate leading-relaxed mb-8 max-w-xl">
                Transform regulatory complexity into operational clarity. AURA — the <strong>Advanced Unified Regulatory Architecture</strong> — connects regulations to data, processes, systems, and change management for regulated organisations.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <a
                  href="https://navigatecompliance.io/contact/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-aura-deep hover:bg-aura-ink text-white font-medium rounded transition-colors"
                  data-testid="request-demo-btn"
                >
                  Request Demo
                  <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  onClick={() => document.getElementById('capabilities')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-aura-deep text-aura-deep hover:bg-aura-fog font-medium rounded transition-colors"
                  data-testid="explore-btn"
                >
                  Explore Platform
                </button>
              </div>
              
              {/* Trust Stats */}
              <div className="flex flex-wrap gap-8">
                {TRUST_INDICATORS.map((stat, i) => (
                  <div key={i} className="text-left">
                    <div className="font-display text-2xl font-bold text-aura-deep">
                      {stat.value}<span className="text-aura-teal">{stat.suffix}</span>
                    </div>
                    <div className="text-xs text-aura-slate mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            {/* Right - Visual */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              {/* Asymmetric "Tetris" blocks */}
              <div className="relative h-[500px]">
                {/* Main block */}
                <div 
                  className="absolute top-0 right-0 w-80 h-64 rounded-2xl p-6 text-white"
                  style={{ backgroundColor: CHROME }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-display font-semibold">Control Framework</div>
                      <div className="text-xs text-white/60">247 Controls Mapped</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/70">Design Effectiveness</span>
                      <span className="font-mono text-aura-teal-bright">94%</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-aura-teal-bright rounded-full" style={{ width: '94%' }} />
                    </div>
                  </div>
                </div>
                
                {/* Secondary block */}
                <div className="absolute top-48 right-24 w-64 h-48 rounded-2xl bg-white border border-border shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="w-4 h-4 text-aura-teal" strokeWidth={1.5} />
                    <span className="text-sm font-medium text-aura-deep">Risk Overview</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="text-xs text-aura-slate">Low Risk</span>
                      <span className="ml-auto font-mono text-xs text-aura-deep">142</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-amber-500" />
                      <span className="text-xs text-aura-slate">Medium Risk</span>
                      <span className="ml-auto font-mono text-xs text-aura-deep">38</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="text-xs text-aura-slate">High Risk</span>
                      <span className="ml-auto font-mono text-xs text-aura-deep">7</span>
                    </div>
                  </div>
                </div>
                
                {/* Tertiary block */}
                <div className="absolute top-72 left-0 w-56 h-40 rounded-2xl bg-aura-fog border border-border p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-aura-teal" strokeWidth={1.5} />
                    <span className="text-sm font-medium text-aura-deep">Regulatory Universe</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {['POPIA', 'FICA', 'FAIS', 'NCA', 'COFI'].map(reg => (
                      <span key={reg} className="px-2 py-1 bg-white border border-border rounded text-[10px] font-mono text-aura-slate">
                        {reg}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Client Segments */}
      <section className="py-12 px-6 lg:px-8 border-y border-border bg-aura-fog/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
            <span className="text-xs text-aura-slate uppercase tracking-wider">Trusted by</span>
            {SEGMENTS.map((segment, i) => (
              <div key={i} className="flex items-center gap-2 text-aura-slate/70">
                <segment.icon className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">{segment.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section id="capabilities" className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-left mb-12"
          >
            <span className="aura-overline text-aura-teal mb-4 block">Platform Capabilities</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-aura-ink mb-4">
              End-to-End Regulatory Intelligence
            </h2>
            <p className="text-aura-slate max-w-2xl">
              From regulatory tracking to audit-ready evidence, AURA provides the complete toolkit for operationalising compliance.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CAPABILITIES.map((cap, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group aura-card aura-card-hover p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-aura-fog flex items-center justify-center mb-4 group-hover:bg-aura-teal/10 transition-colors">
                  <cap.icon className="w-5 h-5 text-aura-teal" strokeWidth={1.5} />
                </div>
                <h3 className="font-display font-semibold text-lg text-aura-deep mb-2">{cap.title}</h3>
                <p className="text-sm text-aura-slate mb-4 leading-relaxed">{cap.description}</p>
                <div className="flex flex-wrap gap-2">
                  {cap.features.map((f, j) => (
                    <span key={j} className="aura-evidence-chip">{f}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Intelligence Section */}
      <section id="intelligence" className="py-20 px-6 lg:px-8 bg-aura-fog/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="aura-overline text-aura-teal mb-4 block">AI-Powered Intelligence</span>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-aura-ink mb-6">
                Supervised AI with<br />
                <span className="text-aura-teal">Human-in-the-Loop</span>
              </h2>
              <p className="text-aura-slate leading-relaxed mb-8">
                Our NAVI intelligence engine combines large language models with critical human oversight. Every AI-generated output is reviewed, validated, and approved by qualified professionals before becoming operational.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium text-aura-deep mb-1">Two-Level Review</h4>
                    <p className="text-sm text-aura-slate">Admin approval → Tenant validation → Operational</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium text-aura-deep mb-1">Explainable Outputs</h4>
                    <p className="text-sm text-aura-slate">Every requirement traces to source provisions</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium text-aura-deep mb-1">Audit Trail</h4>
                    <p className="text-sm text-aura-slate">Complete provenance for regulatory assurance</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aura-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-aura-teal/10 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-aura-teal" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-aura-deep">NAVI Intelligence</div>
                      <div className="text-xs text-aura-slate">Obligation Extraction</div>
                    </div>
                  </div>
                  <span className="aura-immutable-badge">
                    <Lock className="w-3 h-3" /> Audited
                  </span>
                </div>
                
                {/* Sample obligation card */}
                <div className="border border-border rounded-lg p-4 mb-4 aura-probabilistic">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-medium text-aura-teal">Extracted Obligation</span>
                    <span className="text-[10px] font-mono text-aura-slate">FICA s21(1)</span>
                  </div>
                  <p className="text-sm text-aura-deep leading-relaxed">
                    The Accountable Institution must establish and verify the identity of every prospective client before establishing a business relationship.
                  </p>
                </div>
                
                <div className="flex items-center justify-between text-xs">
                  <span className="text-aura-slate">Confidence: <span className="font-mono text-aura-teal">0.94</span></span>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded text-[10px]">Pending Review</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-8" style={{ backgroundColor: CHROME }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-6">
            Ready to Transform Your Compliance Operations?
          </h2>
          <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
            Join leading regulated organisations using AURA to operationalise compliance and demonstrate regulatory assurance.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://navigatecompliance.io/contact/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-aura-deep font-semibold rounded hover:bg-aura-fog transition-colors"
            >
              Schedule a Demo
              <ArrowRight className="w-4 h-4" />
            </a>
            <button
              onClick={handleSignInClick}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white font-medium rounded hover:bg-white/10 transition-colors"
            >
              Sign In to Platform
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-8 border-t border-border bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-aura-deep flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-display font-bold text-aura-deep">AURA</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-aura-slate">
              <a href="https://navigatecompliance.io" target="_blank" rel="noopener noreferrer" className="hover:text-aura-deep transition-colors">
                Navigate Compliance
              </a>
              <a href="https://navigatecompliance.io/contact/" target="_blank" rel="noopener noreferrer" className="hover:text-aura-deep transition-colors">
                Contact
              </a>
              <span>© 2026 Navigate Risk. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Access Modal */}
      <AnimatePresence>
        {showAccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-aura-ink/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowAccessModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="aura-card p-8 max-w-lg w-full"
            >
              {/* Close button */}
              <button
                onClick={() => setShowAccessModal(false)}
                className="absolute top-4 right-4 text-aura-slate hover:text-aura-deep transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-aura-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-aura-teal" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl font-bold text-aura-deep mb-2">
                  Access AURA GRC
                </h3>
                <p className="text-sm text-aura-slate">
                  Choose your access method below
                </p>
              </div>

              {/* Preview Access */}
              <div className="mb-6">
                <span className="aura-overline text-aura-teal mb-3 block">
                  Preview Access · Review, Test & Refine
                </span>
                <button
                  onClick={handleAdminPreviewAccess}
                  disabled={isLoggingIn}
                  className="w-full p-4 bg-aura-fog border-2 border-aura-teal/30 hover:border-aura-teal/60 rounded-lg transition-all group flex items-center justify-between"
                  data-testid="admin-preview-access-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-aura-teal/20 rounded-lg flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-aura-teal" strokeWidth={1.5} />
                    </div>
                    <div className="text-left">
                      <span className="text-aura-deep font-medium block">
                        {isLoggingIn ? 'Accessing...' : 'AURA Admin Preview'}
                      </span>
                      <span className="text-xs text-aura-slate">
                        One-click preview access. Full administrator privileges.
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-aura-slate group-hover:text-aura-teal transition-colors" />
                </button>
                <p className="text-xs text-aura-slate mt-2 px-1">
                  Grants full administrator privileges on the AURA Preview environment for review, testing and refinement.
                </p>
                {loginError && (
                  <p className="text-xs text-red-600 mt-2 px-1">{loginError}</p>
                )}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-aura-slate">OR</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Production Access */}
              <div>
                <span className="aura-overline text-aura-deep mb-3 block">
                  Production Access · Paid Subscriptions
                </span>
                <a
                  href="https://navigatecompliance.io/contact/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full p-4 bg-aura-deep/5 border border-aura-deep/20 hover:border-aura-deep/40 rounded-lg transition-all flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-aura-deep/10 rounded-lg flex items-center justify-center">
                      <Lock className="w-5 h-5 text-aura-deep" strokeWidth={1.5} />
                    </div>
                    <div className="text-left">
                      <span className="text-aura-deep font-medium block">Contact Navigate</span>
                      <span className="text-xs text-aura-slate">Request a demo or subscription</span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-aura-deep" />
                </a>
              </div>

              {/* Close link */}
              <button
                onClick={() => setShowAccessModal(false)}
                className="w-full mt-6 py-3 text-aura-slate hover:text-aura-deep transition-colors text-sm"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AURALanding;
